// Don't need to put getToken on the dependency array
/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode, Dispatch, SetStateAction } from 'react';

// Types
import { EmployeeInfo, EmployeeTraining, EmployeeProject, EmployeeRelationship, EmployeeActivity, EmployeeSkill, EmployeeCertification, EmployeeEducation, EmployeeLunchRegistration, YearReviews } from '@/types/employee';
import { TimelineLoadingState } from '@shared/types';

// Actions
import { getEmployeeProject, getEmployeeInfo, getEmployeeTrainings, getEmployeeLunchRegistration, getEmployeeTimeOffRequests, getEmployeeRelationships, getEmployeeProfile, getUserProfile } from '@/actions/client';

// Constants
import { API_ERROR_MESSAGE, DATE_FORMATS, ROUTES, STATUS_CODE } from '@shared/constants';
import { EMPLOYEE_INFO_RESTRICTED_ROUTES } from '@/constants/employee';

// UIs
import FullScreenLoader from '@/components/FullScreenLoader';
import { LoadingIndicator } from '@shared/ui';

// Utils
import { capitalize, formatDate, formatDateStringToMonthYear } from '@shared/utils';
import { showErrorToast } from '@/utils';
import { getWorkPlaceText, sortTrainingsByStatusAndDate, isRestrictedRoute, mapEmployeeProfileToEmployeeInfo, sortEducationsByGraduation } from '@/utils/employee';

// Hooks
import { useFirebase } from './firebase';
import { useEmployeeWebsocket } from '@/hooks/useEmployeeWebsocket';
import { useGetEmployeeActivities } from '@/hooks/useGetEmployeeActivities';
import { usePathname } from 'next/navigation';

// Types
export type EmployeeLoadingState = {
  info: boolean;
  trainings: boolean;
  projects: boolean;
  technicalSkills: boolean;
  certifications: boolean;
  educations: boolean;
  relationship: boolean;
  timeOffRequests: boolean;
  activities: boolean;
  lunchRegistration: boolean;
};
export type EmployeeContextType = {
  // Employee data
  info: EmployeeInfo | undefined;
  lunchRegistration: EmployeeLunchRegistration | undefined;
  trainings: EmployeeTraining[];
  projects: EmployeeProject[];
  technicalSkills: EmployeeSkill[];
  certifications: EmployeeCertification[];
  educations: EmployeeEducation[];
  relationship: EmployeeRelationship | undefined;
  timeOffRequests: number;
  activities: EmployeeActivity[];
  yearReviews?: YearReviews;

  // Loading states
  loading: EmployeeLoadingState;
  isProcessing: boolean;
  hasFetchedTrainingActivity?: boolean;

  // Individual fetch actions
  fetchInfo: (employeeId: string) => Promise<void>;
  fetchTrainings: (employeeId: string, isSimple?: boolean) => Promise<void>;
  fetchProjects: (employeeId: string) => Promise<void>;
  fetchTimeOffRequests: (employeeId: string) => Promise<void>;
  fetchLunchRegistration: (employeeId: string) => Promise<void>;
  fetchRelationships: (employeeId: string) => Promise<void>;
  fetchAllData: (employeeId: string) => Promise<void>;

  // Set data actions
  setInfo: Dispatch<SetStateAction<EmployeeInfo | undefined>>;
  setTrainings: Dispatch<SetStateAction<EmployeeTraining[]>>;
  setProjects: Dispatch<SetStateAction<EmployeeProject[]>>;
  setTechnicalSkills: Dispatch<SetStateAction<EmployeeSkill[]>>;
  setCertifications: Dispatch<SetStateAction<EmployeeCertification[]>>;
  setEducations: Dispatch<SetStateAction<EmployeeEducation[]>>;
  setRelationship: Dispatch<SetStateAction<EmployeeRelationship | undefined>>;
  setTimeOffRequests: Dispatch<SetStateAction<number>>;
  setActivities: Dispatch<SetStateAction<EmployeeActivity[]>>;
  setLunchRegistration: Dispatch<SetStateAction<EmployeeLunchRegistration | undefined>>;
  setYearReviews: Dispatch<SetStateAction<YearReviews | undefined>>;
  setIsProcessing: Dispatch<SetStateAction<boolean>>;
  setLoading: Dispatch<SetStateAction<EmployeeLoadingState>>;

  // Timeline pagination
  hasMoreEvents: boolean;
  loadingEventsState: TimelineLoadingState;
  eventsError: string | null;
  loadMoreEvents: () => Promise<void>;
  retryEvents: () => Promise<void>;
  fetchActivities: () => Promise<void>;

  // Utils
  isNotFound: boolean;
  setIsNotFound: Dispatch<SetStateAction<boolean>>;
};

// Create context
const EmployeeContext = createContext<EmployeeContextType | undefined>(undefined);
const initialLoadingState: EmployeeLoadingState = {
  info: true,
  trainings: true,
  projects: true,
  technicalSkills: true,
  certifications: true,
  educations: true,
  relationship: true,
  timeOffRequests: true,
  activities: true,
  lunchRegistration: true
};

// Provider props interface
export interface EmployeeProviderProps {
  children: ReactNode;
  employeeId: string;
  initialData?: {
    info?: EmployeeInfo;
    lunchRegistration?: EmployeeLunchRegistration;
    trainings?: EmployeeTraining[];
    projects?: EmployeeProject[];
    technicalSkills?: EmployeeSkill[];
    certifications?: EmployeeCertification[];
    educations?: EmployeeEducation[];
    relationship?: EmployeeRelationship;
    timeOffRequests?: number;
    activities?: EmployeeActivity[];
  };
  isFetchData?: boolean;
}

// Provider component
export const EmployeeProvider = ({
  children,
  employeeId: employeeIdParams,
  initialData,
  isFetchData = true
}: EmployeeProviderProps) => {
  // State
  const [isNotFound, setIsNotFound] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [info, setInfo] = useState<EmployeeInfo | undefined>(initialData?.info);
  const [hasFetchedTrainingActivity, setHasFetchedTrainingActivity] = useState(false);
  const [trainings, setTrainings] = useState<EmployeeTraining[]>(initialData?.trainings || []);
  const [projects, setProjects] = useState<EmployeeProject[]>(initialData?.projects || []);
  const [technicalSkills, setTechnicalSkills] = useState<EmployeeSkill[]>(initialData?.technicalSkills || []);
  const [certifications, setCertifications] = useState<EmployeeCertification[]>(initialData?.certifications || []);
  const [educations, setEducations] = useState<EmployeeEducation[]>(initialData?.educations || []);
  const [relationship, setRelationship] = useState<EmployeeRelationship | undefined>(initialData?.relationship);
  const [timeOffRequests, setTimeOffRequests] = useState<number>(initialData?.timeOffRequests || 0);
  const [activities, setActivities] = useState<EmployeeActivity[]>(initialData?.activities || []);
  const [lunchRegistration, setLunchRegistration] = useState<EmployeeLunchRegistration | undefined>(initialData?.lunchRegistration);
  const [yearReviews, setYearReviews] = useState<YearReviews | undefined>();
  const [loading, setLoading] = useState<EmployeeLoadingState>(initialLoadingState);
  const [isInitialized, setIsInitialized] = useState(false);
  const {
    getToken
  } = useFirebase();
  const pathname = usePathname();

  // Use the employee activities hook for pagination
  const {
    activities: rootActivities,
    hasMore: hasMoreEvents,
    loadingState: loadingEventsState,
    error: eventsError,
    loadMore: loadMoreEvents,
    retry: retryEvents,
    refetchActivities: fetchActivities
  } = useGetEmployeeActivities({
    employeeId: employeeIdParams,
    isInitialized,
    activities,
    setActivities
  });
  const isResourceInaccessible = (status: number | undefined): boolean => {
    return status === STATUS_CODE.NOT_FOUND || status === STATUS_CODE.FORBIDDEN;
  };

  // Fetches employee info: getEmployeeProfile for restricted routes, getEmployeeInfo for others
  const fetchInfo = useCallback(async (employeeId: string) => {
    const token = await getToken();
    // Fetch getUserProfile for status (needed for both restricted and non-restricted routes)
    const {
      data: userData
    } = await getUserProfile({
      username: employeeId,
      requestConfig: {
        token
      }
    });
    const isRestricted = isRestrictedRoute(pathname, EMPLOYEE_INFO_RESTRICTED_ROUTES);

    // Handle restricted routes (participants don't have permission for full employee info)
    if (isRestricted) {
      const {
        data: profileData,
        error: profileError,
        status: profileStatus
      } = await getEmployeeProfile(employeeId, {
        requestConfig: {
          token
        }
      });
      if (isResourceInaccessible(profileStatus)) {
        setIsNotFound(true);
        setLoading(prev => ({
          ...prev,
          info: false
        }));
        return;
      }
      if (profileError || !profileData) {
        showErrorToast(profileError || API_ERROR_MESSAGE.COMMON_ERROR);
        setLoading(prev => ({
          ...prev,
          info: false
        }));
        return;
      }
      const mappedInfo = mapEmployeeProfileToEmployeeInfo(profileData, employeeId);
      setInfo({
        ...mappedInfo,
        status: userData?.status
      });

      // Set loading states for fields not available in restricted routes
      setLoading(prev => ({
        ...prev,
        educations: false,
        certifications: false,
        technicalSkills: false,
        info: false
      }));
      return;
    }

    // Handle non-restricted routes (fetch full employee info)
    const {
      data,
      error,
      status
    } = await getEmployeeInfo(employeeId, {
      requestConfig: {
        token
      }
    });
    if (isResourceInaccessible(status)) {
      setIsNotFound(true);
      setLoading(prev => ({
        ...prev,
        info: false
      }));
      return;
    }
    if (error || !data) {
      showErrorToast(error || API_ERROR_MESSAGE.COMMON_ERROR);
      setLoading(prev => ({
        ...prev,
        info: false
      }));
      return;
    }
    const {
      id,
      info,
      education,
      skills,
      certs,
      yearReviews
    } = data;
    if (info) {
      setInfo({
        ...info,
        email: id,
        status: userData?.status,
        gender: info.gender ? capitalize(info.gender) : undefined,
        room: info.room ? getWorkPlaceText(info.room) : undefined,
        birthday: info.birthday ? formatDateStringToMonthYear(formatDate(info.birthday)) : undefined
      });
    }
    if (yearReviews) setYearReviews(yearReviews);
    if (education) setEducations(sortEducationsByGraduation(education));
    if (skills) setTechnicalSkills(skills);
    if (certs) setCertifications(certs);
    setLoading(prev => ({
      ...prev,
      educations: false,
      certifications: false,
      technicalSkills: false,
      info: false
    }));
  }, [pathname]);
  const fetchRelationships = useCallback(async (employeeId: string) => {
    const token = await getToken();
    const {
      data,
      error,
      status
    } = await getEmployeeRelationships(employeeId, {
      requestConfig: {
        token
      }
    });
    if (isResourceInaccessible(status)) {
      setLoading(prev => ({
        ...prev,
        relationship: false
      }));
      return;
    }
    if (error || !data) {
      showErrorToast(error || API_ERROR_MESSAGE.COMMON_ERROR);
      setLoading(prev => ({
        ...prev,
        relationship: false
      }));
      return;
    }
    setRelationship(data);
    setLoading(prev => ({
      ...prev,
      relationship: false
    }));
  }, []);
  const fetchTrainings = useCallback(async (employeeId: string, isSimple?: boolean) => {
    if (pathname.includes(ROUTES.EMPLOYEES) && trainings.length > 0) return;
    const isSimpleFetch = isSimple ?? !pathname.includes(ROUTES.EMPLOYEE_TRAININGS);
    setHasFetchedTrainingActivity(!isSimpleFetch);
    const token = await getToken();
    const {
      data,
      error,
      status
    } = await getEmployeeTrainings(employeeId, isSimpleFetch, {
      requestConfig: {
        token
      }
    });
    if (isResourceInaccessible(status)) {
      setLoading(prev => ({
        ...prev,
        trainings: false
      }));
      return;
    }
    if (error || !data) {
      showErrorToast(error || API_ERROR_MESSAGE.COMMON_ERROR);
      setLoading(prev => ({
        ...prev,
        trainings: false
      }));
      return;
    }
    const sortedData = sortTrainingsByStatusAndDate(data);
    setTrainings(sortedData);
    setLoading(prev => ({
      ...prev,
      trainings: false
    }));
  }, []);
  const fetchProjects = useCallback(async (employeeId: string) => {
    const token = await getToken();
    const {
      data,
      error,
      status
    } = await getEmployeeProject(employeeId, {
      requestConfig: {
        token
      }
    });
    if (isResourceInaccessible(status)) {
      setLoading(prev => ({
        ...prev,
        projects: false
      }));
      return;
    }
    if (error || !data) {
      showErrorToast(error || API_ERROR_MESSAGE.COMMON_ERROR);
      setLoading(prev => ({
        ...prev,
        projects: false
      }));
      return;
    }
    const formattedData: EmployeeProject[] = data.map(project => ({
      ...project,
      kickOffDate: project.kickOffDate ? formatDate(project.kickOffDate, DATE_FORMATS.FULL_MONTH_DAY_YEAR) : undefined
    }));
    setProjects(formattedData);
    setLoading(prev => ({
      ...prev,
      projects: false
    }));
  }, []);
  const fetchTimeOffRequests = useCallback(async (employeeId: string) => {
    const token = await getToken();
    const {
      data,
      error,
      status
    } = await getEmployeeTimeOffRequests(employeeId, {
      requestConfig: {
        token
      }
    });
    if (isResourceInaccessible(status)) {
      setLoading(prev => ({
        ...prev,
        timeOffRequests: false
      }));
      return;
    }
    if (error || data === null) {
      showErrorToast(error || API_ERROR_MESSAGE.COMMON_ERROR);
      setLoading(prev => ({
        ...prev,
        timeOffRequests: false
      }));
      return;
    }
    setTimeOffRequests(data);
    setLoading(prev => ({
      ...prev,
      timeOffRequests: false
    }));
  }, []);
  const fetchLunchRegistration = useCallback(async (employeeId: string) => {
    const token = await getToken();
    const {
      data,
      error,
      status
    } = await getEmployeeLunchRegistration(employeeId, {
      requestConfig: {
        token
      }
    });
    if (isResourceInaccessible(status)) {
      setLoading(prev => ({
        ...prev,
        lunchRegistration: false
      }));
      return;
    }
    if (error || !data) {
      if (data !== null) showErrorToast(error || API_ERROR_MESSAGE.COMMON_ERROR);
      setLoading(prev => ({
        ...prev,
        lunchRegistration: false
      }));
      return;
    }
    setLunchRegistration(data);
    setLoading(prev => ({
      ...prev,
      lunchRegistration: false
    }));
  }, []);

  // Fetch all data
  const fetchAllData = useCallback(async (employeeId: string) => {
    await Promise.all([fetchInfo(employeeId), fetchTrainings(employeeId), fetchProjects(employeeId), fetchTimeOffRequests(employeeId), fetchLunchRegistration(employeeId), fetchRelationships(employeeId)]);
    // Set isInitialized to trigger the activities hook
    setIsInitialized(true);
  }, [fetchInfo, fetchTrainings, fetchProjects, fetchTimeOffRequests]);
  useEffect(() => {
    // Reset state when employeeId changes to prevent stale data
    setIsNotFound(false);
    setIsInitialized(false);
    setInfo(undefined);
    setTrainings([]);
    setProjects([]);
    setTechnicalSkills([]);
    setCertifications([]);
    setEducations([]);
    setRelationship(undefined);
    setTimeOffRequests(0);
    setActivities([]);
    setLunchRegistration(undefined);
    setYearReviews(undefined);
    setHasFetchedTrainingActivity(false);
    if (isFetchData) {
      setLoading(initialLoadingState);
      fetchAllData(employeeIdParams);
    } else {
      setLoading(() => ({
        info: false,
        trainings: false,
        activityTraining: false,
        projects: false,
        technicalSkills: false,
        certifications: false,
        educations: false,
        relationship: false,
        timeOffRequests: false,
        activities: false,
        lunchRegistration: false
      }));
      setIsInitialized(true);
    }
  }, [fetchAllData, employeeIdParams, isFetchData]);
  const value: EmployeeContextType = {
    info,
    trainings,
    projects,
    technicalSkills,
    certifications,
    educations,
    relationship,
    timeOffRequests,
    lunchRegistration,
    yearReviews,
    activities: rootActivities,
    loading,
    isProcessing,
    hasFetchedTrainingActivity,
    // Fetch data
    fetchInfo,
    fetchTrainings,
    fetchProjects,
    fetchTimeOffRequests,
    fetchAllData,
    fetchLunchRegistration,
    fetchRelationships,
    // Set data
    setInfo,
    setTrainings,
    setProjects,
    setTechnicalSkills,
    setCertifications,
    setEducations,
    setRelationship,
    setTimeOffRequests,
    setActivities,
    setLunchRegistration,
    setYearReviews,
    setIsProcessing,
    setLoading,
    // Timeline pagination
    hasMoreEvents,
    loadingEventsState,
    eventsError,
    loadMoreEvents,
    retryEvents,
    fetchActivities,
    // Utils
    isNotFound,
    setIsNotFound
  };
  useEmployeeWebsocket(value);
  if (loading.info) return <FullScreenLoader />;
  return <EmployeeContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="EmployeeProvider" data-sentry-source-file="employee.tsx">
      {/* Render processing overlay */}
      {isProcessing && <div className="fixed inset-0 bg-background/2 backdrop-blur-sm z-50 flex-center transition-opacity duration-300">
          <LoadingIndicator size="lg" color="primary" center className="min-h-48" />
        </div>}

      {children}
    </EmployeeContext.Provider>;
};

// Custom hook to use the employee context
export const useEmployee = (): EmployeeContextType => {
  const context = useContext(EmployeeContext);
  if (context === undefined) {
    throw new Error('useEmployee must be used within an EmployeeProvider');
  }
  return context;
};